import React, { useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// Components

// Hooks

// Utils
import { trackColorChange as trackColorChangeAction } from '../../actions/products';
import { desktop, mobile } from '../../styles';
import generateColorSwatchUrl from '../../utils/generateColorSwatchUrl';
import {
  getLinkedProduct,
  getProductCategoryTypes,
} from '../../utils/productDetail';
import {
  getClearanceFplId,
  isClearanceItem,
  isPreorderable as checkIsPreorderable,
} from '../../utils/selectors';
import { useMembership } from '../../utils/useMembership';
import useUnlockedXclusive from '../../utils/useUnlockedXclusive';

// Constants

import { Component as ColorSwatch } from '../ColorSwatch';

const hideFromGridTag = config.get('public.productTags.hideFromGrid');
const unlockedXclusiveTag = config.get('public.productTags.unlockedXclusive');
const xtraVipBoxTag = config.get('public.productTags.xtraVipBox');
const leadOnlyTag = config.get('public.productTags.leadOnly');
const vipOnlyTag = config.get('public.productTags.vipOnly');

const StyledColorSwatch = styled(ColorSwatch)`
  ${ColorSwatch.ColorRadioGroup} {
    margin-right: ${props => props.theme.spacing.small * -1}px;
  }

  ${ColorSwatch.Color} {
    margin-bottom: 0;
  }

  ${ColorSwatch.ColorRadioButton} {
    display: flex;
    flex-direction: column;
    width: 32px;
    margin: 0 12px 12px 0;

    ${mobile`
      width: 45px;
    `};
  }

  ${ColorSwatch.Color} {
    height: 32px;
    width: 32px;

    ${mobile`
      height: 45px;
      width: 45px;
    `};
  }

  ${desktop`
    align-self: flex-start;
  `};
`;

function ProductDetailColorSwatches({
  className,
  id,
  isGwpSelection = false,
  isSoldOut,
  isUnbreakableSet,
  isXtraVipBox,
  limit,
  masterProduct,
  onChangeColor,
  selectedColor,
  selectedColorLabel,
  selectedSize,
  setId = null,
  showSelectedOnly = false,
  shouldHideColorSwatches = false,
}) {
  const dispatch = useDispatch();
  const { isVip } = useMembership();
  const isClearanceProduct = useSelector(state =>
    isClearanceItem(state, { product: masterProduct })
  );
  const isPreorderable = useSelector(state =>
    checkIsPreorderable(state, { product: masterProduct })
  );
  const clearanceFplId = useSelector(getClearanceFplId);
  const reviews = useSelector(state => state.reviews);
  const colorList = masterProduct.related_product_id_object_list;
  const { isUnlockedXclusiveProductVisible, shouldSeeUnlockedXclusiveBadge } =
    useUnlockedXclusive();

  const trackColorChange = (newcolor, oldColor, newProductId) => {
    const { counts = {} } = reviews;

    const { reviewCount = 0, recommendedPercentage = 0 } = counts;

    const {
      average_overall_review_filtered_stores: averageRating = 0,
      meta_data: metaData = [],
    } = masterProduct;

    const hasMemberModels = metaData.some(metaData =>
      Boolean(metaData.meta_description_override)
    );

    dispatch(
      trackColorChangeAction({
        is_preorder: isPreorderable,
        avg_recommended: recommendedPercentage,
        review_num: reviewCount,
        has_reviews: Boolean(reviewCount),
        avg_star_rating: Number(averageRating.toFixed(1)),
        has_member_models: hasMemberModels,
        product_id: setId ? setId.toString() : newProductId.toString(),
        previous_color: oldColor,
        new_color: newcolor,
      })
    );
  };

  const handleColorChange = useCallback(
    async (event, color) => {
      trackColorChange(color.name, selectedColorLabel, color.value);
      onChangeColor && onChangeColor(event, color, id, isPreorderable);
    },
    [onChangeColor]
  );

  const getIsColorQuantityAvailable = colors => {
    return colors.some(color => {
      return isPreorderable
        ? color.availablePreorderQuantity > 0
        : color.availableQuantity > 0;
    });
  };

  function getColorOptions() {
    const colorList = masterProduct.related_product_id_object_list;
    // If we are rendering gift with purchase details, we don't need to apply these filters because we hide all color swatches
    // and we don't want the filter tags to apply to gift items in the gwp carousel
    if (isGwpSelection) {
      return colorList.map(colorProduct => ({
        name: colorProduct.color,
        value: colorProduct.related_product_id,
      }));
    }
    return (
      colorList
        // Show vip or lead only colors depending on membership level
        .filter(colorProduct =>
          isVip
            ? !colorProduct.tag_id_list?.includes(leadOnlyTag)
            : !colorProduct.tag_id_list?.includes(vipOnlyTag)
        )
        .filter(colorProduct => {
          const fplIds = colorProduct.featured_product_location_id_list || [];
          if (isClearanceProduct) {
            // show colors only for products on clearance sale
            return fplIds.includes(clearanceFplId);
          }
          // otherwise exclude all colors on clearance sale
          return !fplIds.includes(clearanceFplId);
        })
        .filter(colorProduct => {
          // Only show colors with the `hideFromGrid` tag for unbreakable sets.
          // These special colors should act like a separate product and _only_ show for unbreakable sets.
          if (colorProduct.tag_id_list) {
            const hideFromGrid =
              colorProduct.tag_id_list.includes(hideFromGridTag);
            const isPartOfXtraVipBox =
              colorProduct.tag_id_list.includes(xtraVipBoxTag);
            /* since we no longer check the hideFromGrid tag on individual items for xtra VIP box
             * we will use the xtraVipBoxTag instead
             */
            if (isUnbreakableSet && isXtraVipBox) {
              return isPartOfXtraVipBox;
            } else if (isUnbreakableSet) {
              return hideFromGrid;
            }
            if (!isUnbreakableSet) {
              // FIXME: If you ended up on a single product PDP with only one color that has this hideFromGrid tag, the PDP will essentially be unusable.
              return !hideFromGrid;
            }
          }
          // Return all colors if there is no tag list available
          return true;
        })
        .filter(colorProduct => {
          // If a user is an EMP who has not been billed yet, hide "Unlocked Xclusives"
          // from them. Show for everyone else.
          return !(
            !isUnlockedXclusiveProductVisible &&
            colorProduct.tag_id_list &&
            colorProduct.tag_id_list.includes(unlockedXclusiveTag)
          );
        })
        .map(colorProduct => {
          const skuWithoutSize = colorProduct.item_number.slice(0, -3);
          const isSelected =
            colorProduct.related_product_id === masterProduct.master_product_id;
          if (showSelectedOnly && !isSelected) {
            // Skip it, will be filtered out below.
            return null;
          }
          const isSelectedSizeUnavailable =
            isSelected && selectedSize?.error === 'SIZE_UNAVAILABLE';
          let availableQuantity = 0;
          let availablePreorderQuantity = 0;
          const sizeList = colorProduct.product_id_object_list;
          if (sizeList) {
            const selectedSizeProduct = selectedSize?.value
              ? sizeList.find(item => {
                  return item.size === selectedSize.value;
                })
              : null;
            if (selectedSizeProduct) {
              // FIXME: We don't know if the related items are exclusive, because
              // the API doesn't give us their tag list. So there could be some
              // promo override like `isExclusiveSoldOut` active that we don't
              // know whether to apply here. This at least applies it to the
              // currently selected product's swatch...
              availableQuantity =
                isSoldOut || isSelectedSizeUnavailable
                  ? 0
                  : selectedSizeProduct.available_quantity;
              availablePreorderQuantity =
                isSoldOut || isSelectedSizeUnavailable
                  ? 0
                  : selectedSizeProduct.available_quantity_preorder;
            } else if (isSoldOut || isSelectedSizeUnavailable) {
              // If no size is selected but the product is deliberately set to sold out,
              // Or if size is selected but the product is unavailable,
              // show the crossed-out state for the color swatch
              availableQuantity = 0;
              availablePreorderQuantity = 0;
            } else if (!selectedSize?.value) {
              for (const product of sizeList) {
                availableQuantity += product.available_quantity;
                availablePreorderQuantity +=
                  product.available_quantity_preorder;
              }
            }
          }

          const linkedProduct = getLinkedProduct(colorProduct);

          const {
            product_category_id_list: productCategoryIdList,
            tag_id_list: tagIdList,
          } = masterProduct;
          const { isBra } = getProductCategoryTypes(
            productCategoryIdList,
            tagIdList
          );

          return {
            availableQuantity,
            availablePreorderQuantity,
            colorUrl: generateColorSwatchUrl({ skuWithoutSize }),
            datePreorderExpires: colorProduct.date_preorder_expires,
            isBra: isBra,
            isUnlockedXclusive:
              colorProduct.tag_id_list &&
              colorProduct.tag_id_list.includes(unlockedXclusiveTag) &&
              shouldSeeUnlockedXclusiveBadge &&
              isUnlockedXclusiveProductVisible,
            itemNumber: colorProduct.item_number, // `itemNumber` is needed to update the ProductImage
            name: colorProduct.color,
            permalink: colorProduct.permalink
              ? colorProduct.permalink
              : linkedProduct.permalink,
            linkedProduct,
            productId: colorProduct.related_product_id
              ? colorProduct.related_product_id
              : linkedProduct.master_product_id,
            sizes: colorProduct.product_id_object_list,
            value: colorProduct.related_product_id
              ? colorProduct.related_product_id
              : linkedProduct.master_product_id,
          };
        })
        // Filter the skipped swatches above.
        .filter(swatch => swatch)
    );
  }

  if (!colorList) {
    return null;
  }

  const colors = getColorOptions();
  const isColorQuantityAvailable = getIsColorQuantityAvailable(colors);

  return (
    <StyledColorSwatch
      className={className}
      showName
      colors={colors}
      value={selectedColor}
      isColorQuantityAvailable={isColorQuantityAvailable}
      isPreorderable={isPreorderable}
      onChange={handleColorChange}
      shouldHideColorSwatches={shouldHideColorSwatches}
    />
  );
}

ProductDetailColorSwatches.propTypes = {
  className: PropTypes.string,
  id: PropTypes.number,
  isGwpSelection: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  /** Is the master product set an unbreakable set? (like the Xtra Vip Box) */
  isUnbreakableSet: PropTypes.bool,
  isXtraVipBox: PropTypes.bool,
  limit: PropTypes.number,
  /** This is a master product in the set or an individual master product
   * (in other words, it is never a master product set)
   */
  masterProduct: PropTypes.object,
  onChangeColor: PropTypes.func,
  selectedColor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedColorLabel: PropTypes.string,
  selectedSize: PropTypes.shape({
    color: PropTypes.object,
    error: PropTypes.object,
    hasMultipleOneSizes: PropTypes.bool,
    masterProductId: PropTypes.number,
    size: PropTypes.object,
    value: PropTypes.string,
  }),
  setId: PropTypes.number,
  showSelectedOnly: PropTypes.bool,
  shouldHideColorSwatches: PropTypes.bool,
};

export default ProductDetailColorSwatches;
